import { ApiRefreshError, SilentHttpError } from '@oppy/omniv2-core';
import { useToastContext } from '@oppy/omniv2-ui';
import * as Sentry from '@sentry/react';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import React, { useCallback, useState } from 'react';

type Props = {
  children: React.ReactNode;
};

const DEFAULT_STALE_TIME_IN_MILLISECONDS = 60000; // 1 minute

export default function QueryProvider({ children }: Props) {
  const { showMessage } = useToastContext();

  const onError = useCallback(
    (error: Error) => {
      if (error instanceof ApiRefreshError) {
        window.location.href = import.meta.env.VITE_OMNILOGIN_URL;
        return;
      }
      if (error instanceof SilentHttpError) return;

      showMessage({
        severity: 'error',
        summary: 'Erreur...',
        detail: error.message,
      });

      Sentry.captureException(new Error(error.message));
    },
    [showMessage],
  );

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: DEFAULT_STALE_TIME_IN_MILLISECONDS,
            refetchOnWindowFocus: false,
          },
        },
        queryCache: new QueryCache({
          onError,
        }),
        mutationCache: new MutationCache({
          onError,
        }),
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}
